import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout, { LegalSection, LegalDisclaimer, LegalLastUpdated } from '../LegalLayout';
import { APP_NAME } from '../../config/branding';
import { LEGAL_ENTITY, MIN_AGE_YEARS } from '../legalConstants';

export default function RegionalCompliancePage() {
  return (
    <LegalLayout
      title="Regional Compliance"
      subtitle={`Location-specific legal notes for users of ${APP_NAME}.`}
    >
      <LegalLastUpdated />

      <LegalSection title="Global baseline">
        <p>
          {APP_NAME} is operated by {LEGAL_ENTITY}. All users must meet our minimum age requirement of {MIN_AGE_YEARS}
          years and comply with our core{' '}
          <Link to="/terms" className="legal-inline-link">Terms</Link>,{' '}
          <Link to="/privacy" className="legal-inline-link">Privacy Policy</Link>, and{' '}
          <Link to="/legal/code-of-conduct" className="legal-inline-link">Code of Conduct</Link>. Additional laws in
          your country may impose further obligations on you as a user and on us as a service provider.
        </p>
      </LegalSection>

      <LegalSection title="European Economic Area (EEA), UK, and Switzerland">
        <ul>
          <li><strong>GDPR / UK GDPR:</strong> You may have rights to access, rectify, erase, restrict, or port personal
            data, and to object to certain processing. <Link to="/contact" className="legal-inline-link">Contact</Link> form to exercise these rights.</li>
          <li><strong>Legal basis:</strong> We process data as described in our Privacy Policy, relying on contract,
            legitimate interests, legal obligation, and consent where applicable.</li>
          <li><strong>Data Protection Authority:</strong> You may lodge a complaint with your local supervisory authority.</li>
          <li><strong>Cookies and similar technologies:</strong> Essential session and security cookies support authentication;
            analytics practices will be disclosed as they are introduced.</li>
        </ul>
      </LegalSection>

      <LegalSection title="India">
        <ul>
          <li><strong>IT Rules & digital intermediaries:</strong> We maintain grievance and reporting mechanisms via{' '}
            our <Link to="/legal/safety" className="legal-inline-link">Safety & Reporting</Link> page.</li>
          <li><strong>DPDP Act:</strong> Where applicable, Indian users may contact us regarding personal data processing
            and consent.</li>
          <li><strong>Local content restrictions:</strong> Content prohibited under Indian law must not be posted; we may
            remove such content upon valid notice.</li>
        </ul>
      </LegalSection>

      <LegalSection title="United States">
        <ul>
          <li><strong>State privacy laws:</strong> Residents of California, Virginia, Colorado, Connecticut, Utah, and
            other states with comprehensive privacy laws may have additional rights (access, deletion, correction,
            opt-out of certain processing). Submit requests to <Link to="/contact" className="legal-inline-link">Contact</Link> form.</li>
          <li><strong>COPPA:</strong> We do not knowingly collect data from children under {MIN_AGE_YEARS}.</li>
          <li><strong>DMCA:</strong> Copyright holders may send takedown notices as described in our{' '}
            <Link to="/legal/fair-use" className="legal-inline-link">Fair Use Policy</Link>.</li>
          <li><strong>Section 230 (information content):</strong> Users are responsible for their content; we moderate
            as described in our policies but do not pre-screen all submissions.</li>
        </ul>
      </LegalSection>

      <LegalSection title="Australia, Canada, and other regions">
        <ul>
          <li><strong>Australia (Privacy Act / Online Safety):</strong> Report harmful material through our safety
            channels; we cooperate with valid regulatory requests.</li>
          <li><strong>Canada (PIPEDA / provincial laws):</strong> Canadian users may request access to and correction of
            personal information.</li>
          <li><strong>Other countries:</strong> You are responsible for complying with local laws when using {APP_NAME}.
            If local law conflicts with these Terms, local mandatory consumer protections may apply to the extent required.</li>
        </ul>
      </LegalSection>

      <LegalSection title="Sanctions and export controls">
        <p>
          You may not use {APP_NAME} if you are located in, or ordinarily resident in, a country or region subject to
          comprehensive sanctions, or if you are on applicable denied-party lists — unless authorized by law.
        </p>
      </LegalSection>

      <LegalSection title="Language">
        <p>
          These materials are published in English. Translations may be provided in the future. If a translated version
          conflicts with the English version, the English version governs unless local law requires otherwise.
        </p>
      </LegalSection>

      <LegalSection title="Contact">
        <p>
          Regional compliance inquiries via the <Link to="/contact" className="legal-inline-link">Contact</Link> form.
        </p>
      </LegalSection>

      <LegalDisclaimer />
    </LegalLayout>
  );
}
